import { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Download, Eye, CheckCircle, AlertCircle, UserPlus, XCircle, Server, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { downloadReceipt } from '../lib/receipt';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const statusBadge = {
  pubblicato: { label: 'Pubblicato', cls: 'bg-green-100 text-green-700', Icon: CheckCircle },
  da_completare: { label: 'Da completare', cls: 'bg-amber-100 text-amber-700', Icon: AlertCircle },
  ignorato: { label: 'Ignorato', cls: 'bg-slate-100 text-slate-500', Icon: XCircle },
};

const SubjectRow = ({ atto, s, onDone }) => {
  const [form, setForm] = useState({ email: '', name: s.nome || '' });
  const [busy, setBusy] = useState(false);
  const badge = statusBadge[s.status];
  const act = async (path, body) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/atti/${atto.id}/soggetti/${s.key}/${path}`, body, { headers: authHeader() });
      onDone();
    } catch (err) {
      alert(err.response?.data?.detail || 'Operazione non riuscita');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border-t py-3 flex flex-col lg:flex-row lg:items-center gap-3" data-testid={`atto-subject-${atto.numero_atto}-${s.key}`}>
      <div className="lg:w-64">
        <p className="font-medium text-sm">{s.nome || 'Nome non rilevato'}</p>
        <p className="text-xs font-mono text-slate-500">{s.ids.join(' · ')} · {s.tipo === 'azienda' ? 'Azienda' : 'Privato'}</p>
      </div>
      <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full w-fit ${badge.cls}`}>
        <badge.Icon className="w-3 h-3" /> {badge.label}
      </span>
      {s.status === 'da_completare' && (
        <div className="flex flex-col sm:flex-row gap-2 lg:ml-auto">
          <Input placeholder="Nome e cognome / ragione sociale" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-9 sm:w-56" data-testid={`subject-name-${s.key}`} />
          <Input type="email" placeholder="Email del cliente" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-9 sm:w-56" data-testid={`subject-email-${s.key}`} />
          <Button size="sm" disabled={busy || !form.email || !form.name} onClick={() => act('completa', form)} className="bg-[#283880]" data-testid={`subject-complete-${s.key}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />} Crea accesso e pubblica
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => act('ignora', {})} data-testid={`subject-ignore-${s.key}`}>Ignora</Button>
        </div>
      )}
    </div>
  );
};

const AttoCard = ({ atto, onChange }) => {
  const markSeen = async () => {
    await axios.post(`${API}/admin/atti/${atto.id}/visto`, {}, { headers: authHeader() });
    onChange();
  };
  return (
    <div className={`bg-white rounded-xl shadow-sm p-5 ${atto.reviewed ? '' : 'ring-2 ring-[#C9A227]/50'}`} data-testid={`atto-card-${atto.numero_atto}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-2">
        <FileText className="w-5 h-5 text-[#283880]" />
        <div>
          <p className="font-semibold text-[#283880]">{atto.numero_atto} {!atto.reviewed && <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#C9A227] text-white px-2 py-0.5 rounded">Nuovo</span>}</p>
          <p className="text-xs text-slate-500">{atto.titolo} · ricevuto il {new Date(atto.received_at).toLocaleString('it')} · versione {atto.versione}</p>
        </div>
        <div className="flex gap-2 sm:ml-auto">
          <Button size="sm" variant="outline" onClick={() => downloadReceipt(`${API}/admin/atti/${atto.id}/pdf`)} data-testid={`atto-pdf-${atto.numero_atto}`}><Download className="w-4 h-4 mr-1" /> PDF</Button>
          {!atto.reviewed && <Button size="sm" variant="outline" onClick={markSeen} data-testid={`atto-seen-${atto.numero_atto}`}><Eye className="w-4 h-4 mr-1" /> Segna come visto</Button>}
        </div>
      </div>
      {atto.soggetti.length === 0
        ? <p className="text-sm text-slate-400 border-t pt-3">Nessun codice fiscale o partita IVA trovato nell'atto.</p>
        : atto.soggetti.map((s) => <SubjectRow key={s.key} atto={atto} s={s} onDone={onChange} />)}
    </div>
  );
};

export const AdminAttiTab = ({ onCountChange, onDataChange }) => {
  const [atti, setAtti] = useState([]);
  const load = async (refreshParent = false) => {
    const res = await axios.get(`${API}/admin/atti`, { headers: authHeader() });
    setAtti(res.data);
    onCountChange?.(res.data.filter((a) => !a.reviewed || a.soggetti.some((s) => s.status === 'da_completare')).length);
    if (refreshParent) onDataChange?.();
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="admin-atti-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#283880]">Atti dal server dello studio</h2>
          <p className="text-sm text-slate-500">Pubblicati in automatico ai clienti riconosciuti dal codice fiscale o dalla partita IVA.</p>
        </div>
        <Button variant="outline" onClick={() => downloadReceipt(`${API}/admin/sync-agent`).catch(() => alert('Download non riuscito'))} data-testid="download-sync-agent-btn">
          <Server className="w-4 h-4 mr-2" /> Programma per il server
        </Button>
      </div>
      <div className="space-y-4">
        {atti.length === 0
          ? <div className="bg-white rounded-xl p-8 text-center text-slate-400">Nessun atto ricevuto. Installi il programma sul server dello studio.</div>
          : atti.map((a) => <AttoCard key={a.id} atto={a} onChange={() => load(true)} />)}
      </div>
    </div>
  );
};
